import { createFileRoute } from '@tanstack/react-router';
import { TableExplorer, type ExplorerTable } from '~/components/admin/TableExplorer';
import { IntegrationsPanel } from '~/components/admin/IntegrationsPanel';
import { AuditAlertsToggle } from '~/components/admin/AuditAlertsToggle';
import { channelKeyFor } from '~/components/job/lead-options';

const SETTINGS_TABLES: ExplorerTable[] = [
  { key: 'roles',         label: 'Roles' },
  { key: 'job_statuses',  label: 'Job Statuses' },
  { key: 'tags',          label: 'Tags' },
  { key: 'insurance_carriers', label: 'Carriers' },
  // Drive-time origins. Flip `is_headquarters` to move the default "Drive Time
  // from PDQ" starting point; add rows as PDQ opens more shops.
  { key: 'pdq_locations', label: 'PDQ Locations' },
  // FB29 — the Lead Channel dropdown on Job Information. New rows land as
  // source_mode 'list' (options come from Lead Sources below); edit the cell to
  // 'plumbers' | 'customers' | 'external_contacts' to point the channel at a
  // live contact list instead (with `contact_kind` = the external_contacts
  // role_hint to filter by).
  // ⚠️ Renaming a channel's `label` orphans the Lead Sources rows whose
  // `channel_key` names the old label — same caveat as a job_statuses
  // `status_key`. Rename both together or that channel's picklist empties.
  {
    key: 'lead_channels',
    label: 'Lead Channels',
    newRow: () => {
      const label = window.prompt('New lead channel name (e.g. "Restoration Referral")')?.trim();
      if (!label) return null;
      return {
        channel_key: channelKeyFor(label),
        label,
        source_mode: 'list',
        is_active: true,
        sort_order: 999,
      };
    },
  },
  {
    key: 'lead_sources',
    label: 'Lead Sources',
    newRow: () => {
      const channel = window.prompt('Which lead channel does this belong to? (exact channel name, e.g. "Marketing Department")')?.trim();
      if (!channel) return null;
      const label = window.prompt('New lead source name (e.g. "Angi")')?.trim();
      if (!label) return null;
      return { channel_key: channel, label, is_active: true, sort_order: 999 };
    },
  },
  // Room dollars and multipliers behind the suggested Job Size on the job page.
  { key: 'job_size_factors', label: 'Job Size Factors' },
  // Cause-of-loss catalog behind Job Information → Source of Damage. Causes
  // typed in on a job land here under 'Uncategorized' — re-file them.
  { key: 'damage_causes',    label: 'Damage Causes' },
  // FB48 — the "Covered?" verdict on a job's Claim Information. `source_key` is
  // matched (case-insensitively, word-wise) against the claim's Source of Loss,
  // falling back to the job's Source of Damage; `label` is what staff see and
  // pick. Verdicts: covered = does the policy pay; depends = "only with the
  // endorsement / only if the facts hold up", which WINS over covered and puts
  // its reason in `note` (shown on hover on the job page).
  // Carrier overrides: leave `carrier_id` blank for the rule everyone gets, or
  // paste a carrier's id from the Carriers tab to make a rule that applies to —
  // and outranks the default for — only that carrier.
  {
    key: 'coverage_rules',
    label: 'Coverage Rules',
    editableIds: ['carrier_id'],
    newRow: () => {
      const label = window.prompt('Cause of loss (e.g. "Sewer / drain backup")')?.trim();
      if (!label) return null;
      const verdict = window.prompt(
        'Is it covered? Type: yes / no / depends', 'no',
      )?.trim().toLowerCase();
      if (!verdict) return null;
      return {
        source_key: label.toLowerCase(),
        label,
        covered: verdict.startsWith('y'),
        depends: verdict.startsWith('d'),
        note: null,
        is_active: true,
        sort_order: 999,
      };
    },
  },
];

export const Route = createFileRoute('/_app/admin/settings')({
  component: () => (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Settings</h1>
      <p className="text-sm text-[var(--color-ink-soft)] mt-1 mb-6">
        Configurable lists. Renaming a role <em>label</em> is fine; renaming the
        <code className="font-mono"> role_key</code> would break compliance workflows
        that reference it — change at your own risk. Same for a Lead Channel
        <em> label</em>: the Lead Sources under it are matched by that name, so rename
        both together.
      </p>
      <IntegrationsPanel />
      <AuditAlertsToggle />
      <TableExplorer tables={SETTINGS_TABLES} />
    </div>
  ),
});
